/*
 * Back9 quiz — storefront runtime.
 *
 * This is the visual quiz and nothing else: it reads a definition, walks the
 * branch graph in the browser, and renders. It never awaits anything to move
 * forward.
 *
 * It writes one thing to browser storage: the answers taken so far, on the
 * reader's own device, so that a reload does not cost him the quiz. Those are
 * answer keys only, they expire in two hours, and they are never sent
 * anywhere. Consented analytics remains entirely separate from this.
 *
 * Consented analytics is a separate, optional file (quiz-tracking.js). If it
 * was not loaded, was not permitted, or fails outright, every line below
 * behaves identically — a person can always answer and always reach the
 * product.
 *
 * Contract: back9-experiments/docs/QUIZ_BACKEND_CONTRACT.md
 */
(() => {
  'use strict';

  if (window.Back9QuizLoaded) return;
  window.Back9QuizLoaded = true;

  const CDN_PREFIX = 'https://cdn.shopify.com/s/files/';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const announce = (message) => window.Back9Theme?.announce?.(message);

  /* ------------------------------------------------------------------ *
   * Safe value handling
   *
   * Definition text is plain text and is only ever applied with
   * textContent. URLs are the one place a definition could reach outside
   * the page, so both media and destinations are checked here.
   * ------------------------------------------------------------------ */

  const text = (value) => (typeof value === 'string' ? value : '');

  // Images may come from three places and no others: a root-relative path, the
  // Shopify files CDN, or the store's own origin.
  //
  // That third case is the one this originally missed. On a store served from
  // its own domain, asset_url hands back "//back9relief.com/cdn/shop/t/3/..."
  // rather than a cdn.shopify.com URL, so every theme-asset drawing failed the
  // prefix test and was dropped without a word. The guard is still a strict
  // allowlist — it just no longer treats the shop's own domain as foreign.
  const mediaUrl = (url) => {
    if (typeof url !== 'string') return null;
    if (/[\s<>"']/.test(url)) return null;
    if (/^\/[^/\\]/.test(url)) return url;

    const absolute = url.startsWith('//') ? `https:${url}` : url;
    if (absolute.startsWith(CDN_PREFIX)) return absolute;

    try {
      const parsed = new URL(absolute, window.location.origin);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
      return parsed.origin === window.location.origin ? parsed.href : null;
    } catch (error) {
      return null;
    }
  };

  const MEDIA_TYPES = new Set(['SHOPIFY_CDN_IMAGE', 'THEME_ASSET']);

  const normalizeMedia = (media) => {
    if (!media || !MEDIA_TYPES.has(media.type)) return null;
    const url = mediaUrl(media.url);
    return url ? { url, alt: text(media.alt) } : null;
  };

  // Root-relative single-slash paths only. No scheme, no protocol-relative
  // escape, no backslash tricks.
  const safePath = (path) => {
    if (typeof path !== 'string') return null;
    if (!/^\/[^/\\]/.test(path)) return null;
    if (/[\s<>"']/.test(path)) return null;
    return path;
  };

  /* ------------------------------------------------------------------ *
   * Definition model
   * ------------------------------------------------------------------ */

  const buildModel = (payload) => {
    const quiz = payload?.quiz;
    const definition = quiz?.definition;
    if (!quiz || !definition || !Array.isArray(definition.questions)) return null;

    const errors = [];
    const questions = new Map();
    const outcomes = new Map();

    definition.questions.forEach((raw) => {
      if (!raw || typeof raw.key !== 'string') return;
      if (questions.has(raw.key)) errors.push(`Duplicate question key "${raw.key}".`);
      const answers = (Array.isArray(raw.answers) ? raw.answers : [])
        .filter((answer) => answer && typeof answer.key === 'string' && answer.next)
        .map((answer) => ({
          key: answer.key,
          label: text(answer.label),
          next: { type: answer.next.type, key: answer.next.key }
        }));

      if (answers.length === 0) errors.push(`Question "${raw.key}" has no answers.`);
      if (answers.length > 8) errors.push(`Question "${raw.key}" has more than 8 answers.`);

      // Answer keys are what analytics is recorded against, so a blank or
      // repeated one is worth naming rather than silently miscounting.
      const seen = new Set();
      answers.forEach((answer) => {
        if (!answer.key) errors.push(`Question "${raw.key}" has an answer with no text.`);
        else if (seen.has(answer.key)) errors.push(`Question "${raw.key}" repeats the answer "${answer.label}".`);
        seen.add(answer.key);
      });

      questions.set(raw.key, {
        key: raw.key,
        prompt: text(raw.prompt),
        supportingText: text(raw.supportingText),
        media: normalizeMedia(raw.media),
        answers
      });
    });

    (Array.isArray(definition.outcomes) ? definition.outcomes : []).forEach((raw) => {
      if (!raw || typeof raw.key !== 'string') return;
      if (outcomes.has(raw.key)) errors.push(`Duplicate outcome key "${raw.key}".`);
      outcomes.set(raw.key, {
        key: raw.key,
        eyebrow: text(raw.eyebrow),
        title: text(raw.title),
        body: text(raw.body),
        protocolHeading: text(raw.protocolHeading),
        protocolSteps: text(raw.protocolSteps),
        protocolClose: text(raw.protocolClose),
        recapOutlook: text(raw.recapOutlook),
        ctaLabel: text(raw.ctaLabel),
        destinationPath: safePath(raw.destinationPath),
        media: normalizeMedia(raw.media)
      });
      if (raw.destinationPath && !safePath(raw.destinationPath)) {
        errors.push(`Outcome "${raw.key}" has a destination that is not a store path.`);
      }
    });

    if (questions.size > 40) errors.push('More than 40 questions.');
    if (outcomes.size > 20) errors.push('More than 20 outcomes.');

    const startQuestionKey = definition.startQuestionKey;
    if (!questions.has(startQuestionKey)) errors.push(`Start question "${startQuestionKey}" does not exist.`);

    questions.forEach((question) => {
      question.answers.forEach((answer) => {
        const exists = answer.next.type === 'OUTCOME'
          ? outcomes.has(answer.next.key)
          : questions.has(answer.next.key);
        if (!exists) errors.push(`Answer "${answer.label || answer.key}" points at a missing "${answer.next.key}".`);
      });
    });

    // Reachability from the start node.
    const reachable = new Set();
    const queue = questions.has(startQuestionKey) ? [startQuestionKey] : [];
    while (queue.length) {
      const key = queue.shift();
      if (reachable.has(key)) continue;
      reachable.add(key);
      questions.get(key)?.answers.forEach((answer) => {
        if (answer.next.type === 'QUESTION' && questions.has(answer.next.key)) queue.push(answer.next.key);
      });
    }
    questions.forEach((question) => {
      if (!reachable.has(question.key)) errors.push(`Question "${question.key}" cannot be reached.`);
    });

    // Shortest remaining depth, by relaxation. A question with no value here
    // has no branch that ever terminates.
    const shortest = new Map();
    for (let pass = 0; pass < questions.size + 1; pass += 1) {
      let changed = false;
      questions.forEach((question) => {
        let best = Infinity;
        question.answers.forEach((answer) => {
          if (answer.next.type === 'OUTCOME') {
            best = Math.min(best, 1);
          } else {
            const next = shortest.get(answer.next.key);
            if (typeof next === 'number') best = Math.min(best, next + 1);
          }
        });
        if (best !== Infinity && best !== shortest.get(question.key)) {
          shortest.set(question.key, best);
          changed = true;
        }
      });
      if (!changed) break;
    }
    questions.forEach((question) => {
      if (reachable.has(question.key) && !shortest.get(question.key)) {
        errors.push(`Question "${question.key}" has no branch that reaches an outcome.`);
      }
    });

    // Longest remaining depth, which is what progress is estimated from. The
    // shortest path would tell someone standing on a three-question branch
    // that they are on the last question. The longest never overshoots: the
    // count can finish early, but it never counts past its own total.
    const remaining = new Map();
    const visiting = new Set();
    let cyclic = false;

    const depthFrom = (key) => {
      if (remaining.has(key)) return remaining.get(key);
      if (visiting.has(key)) {
        cyclic = true;
        return 1;
      }
      visiting.add(key);

      let best = 1;
      questions.get(key)?.answers.forEach((answer) => {
        if (answer.next.type === 'QUESTION' && questions.has(answer.next.key)) {
          best = Math.max(best, depthFrom(answer.next.key) + 1);
        }
      });

      visiting.delete(key);
      remaining.set(key, best);
      return best;
    };

    questions.forEach((question) => depthFrom(question.key));
    if (cyclic) errors.push('The questions form a loop, so a branch never reaches an outcome.');
    if (questions.has(startQuestionKey) && remaining.get(startQuestionKey) > 40) {
      errors.push('The quiz is deeper than 40 questions.');
    }

    return {
      key: quiz.key,
      name: text(quiz.name),
      definitionVersion: quiz.definitionVersion,
      definitionHash: quiz.definitionHash,
      startQuestionKey,
      questions,
      outcomes,
      remaining,
      challenge: payload.challenge || { provider: 'NONE' },
      errors
    };
  };


  /* ------------------------------------------------------------------ *
   * UI
   * ------------------------------------------------------------------ */

  const element = (tag, className, textContent) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (textContent) writeCopy(node, textContent);
    return node;
  };

  const SVG_NS = 'http://www.w3.org/2000/svg';

  const svgNode = (tag, attributes) => {
    const node = document.createElementNS(SVG_NS, tag);
    Object.keys(attributes).forEach((name) => node.setAttribute(name, attributes[name]));
    return node;
  };

  // A caution mark for the line the recap lands on: a solid green disc with a
  // white mark cut out of it, which carries at a glance in a way an outline
  // does not. It stays in the brand's green rather than a warning red — the
  // claim is "here is what your answers add up to", a finding rather than an
  // alarm, and a man who has already bought inserts that did nothing would be
  // right to distrust a red triangle. It carries no label of its own: the
  // sentence beside it says the thing, and a screen reader hears it once.
  const alertMark = () => {
    const svg = svgNode('svg', {
      class: 'quiz__recap-mark',
      viewBox: '0 0 24 24',
      fill: 'none',
      'aria-hidden': 'true',
      focusable: 'false'
    });
    svg.append(
      svgNode('circle', { cx: '12', cy: '12', r: '12', fill: 'currentColor' }),
      svgNode('path', {
        d: 'M12 5.9V13.4',
        stroke: 'var(--q-color-button-text, #FFFFFF)',
        'stroke-width': '2.6',
        'stroke-linecap': 'round'
      }),
      svgNode('circle', { cx: '12', cy: '17.6', r: '1.55', fill: 'var(--q-color-button-text, #FFFFFF)' })
    );
    return svg;
  };

  // Copy is authored in the theme editor, where stars are the only markup
  // anyone can reasonably be expected to type: **bold** and *italic*.
  // Everything is appended as text nodes, so a stray angle bracket in an
  // answer stays an angle bracket rather than becoming a tag.
  // **bold**, *italic*, ***both***, and ==brand== for a phrase set in the
  // brand colour, which is how a product name is picked out of body copy.
  const INLINE_MARKUP = /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*|==[^=]+==)/g;

  const writeInline = (node, line) => {
    line.split(INLINE_MARKUP).forEach((part) => {
      if (!part) return;
      if (part.startsWith('==') && part.endsWith('==')) {
        node.append(element('span', 'quiz__accent', part.slice(2, -2)));
      } else if (part.startsWith('***') && part.endsWith('***')) {
        const strong = element('strong', null);
        strong.append(element('em', null, part.slice(3, -3)));
        node.append(strong);
      } else if (part.startsWith('**') && part.endsWith('**')) {
        node.append(element('strong', null, part.slice(2, -2)));
      } else if (part.startsWith('*') && part.endsWith('*')) {
        node.append(element('em', null, part.slice(1, -1)));
      } else {
        node.append(document.createTextNode(part));
      }
    });
  };

  // A newline in a setting is a break the author asked for. Each authored
  // line becomes its own block so it reads as a separate beat — a plain <br>
  // gives it the same spacing as an accidental wrap, which makes a deliberate
  // three-line passage look like one run-on paragraph.
  const writeCopy = (node, copy) => {
    const text = String(copy);
    if (!text.includes('*') && !text.includes('\n')) {
      node.textContent = text;
      return;
    }
    node.textContent = '';
    const lines = text.split('\n');
    if (lines.length === 1) {
      writeInline(node, text);
      return;
    }
    lines.forEach((line) => node.append(element('span', 'quiz__copy-line', line)));
  };

  const image = (media, className) => {
    const node = element('img', className);
    node.src = media.url;
    node.alt = media.alt;
    // A quiz image is only built when its screen is being shown, so it is
    // already on screen the moment it exists. Deferring it buys nothing and
    // costs a visible pop-in a beat after the answer lands.
    node.loading = 'eager';
    node.decoding = 'async';
    return node;
  };

  const createQuiz = (root) => {
    if (root.dataset.back9QuizInitialized === 'true') return;
    root.dataset.back9QuizInitialized = 'true';

    // A setting left empty in the editor arrives as null, so every string the
    // renderer reads gets a usable default here rather than at each use.
    const authored = JSON.parse(root.querySelector('[data-quiz-config]')?.textContent || '{}');
    Object.keys(authored).forEach((key) => {
      if (authored[key] === null) delete authored[key];
    });

    const config = Object.assign({
      mode: 'proxy',
      proxyBase: '/apps/back9-experiments',
      quizKey: 'golf_fit_quiz',
      showProgress: true,
      startLabel: 'Start the quiz',
      backLabel: 'Back',
      restartLabel: 'Retake the quiz',
      progressText: 'Step [[ current ]] of [[ total ]]',
      progressCompleteText: 'Your result',
      continueLabel: 'Continue',
      loadingHeading: 'Loading your quiz…',
      errorHeading: 'The quiz isn’t available right now',
      errorText: '',
      fallbackLabel: 'See the product',
      sectionId: 'quiz'
    }, authored);

    const inlineDefinition = root.querySelector('[data-quiz-definition]');
    const stage = root.querySelector('[data-quiz-stage]');
    const progress = root.querySelector('[data-quiz-progress]');
    const progressMeter = root.querySelector('[data-quiz-progress-meter]');
    const progressBar = root.querySelector('[data-quiz-progress-bar]');
    const progressLabel = root.querySelector('[data-quiz-progress-label]');
    const backSlot = root.querySelector('[data-quiz-back-slot]');
    const countLabel = root.querySelector('[data-quiz-count]');
    const challengeSlot = root.querySelector('[data-quiz-challenge]');
    const card = root.querySelector('[data-quiz-card]');

    const api = window.Back9QuizApi.create({
      mode: config.mode,
      proxyBase: config.proxyBase,
      mock: {
        definition: inlineDefinition ? JSON.parse(inlineDefinition.textContent || 'null') : null,
        latencyMs: config.mockLatencyMs,
        failureRate: config.mockFailureRate
      }
    });

    let model = null;
    let tracker = null;
    // An optional spreadsheet logger, entirely separate from the tracker above.
    // Either, both, or neither may be present; the quiz does not care.
    let sheet = null;
    let path = []; // [{ questionKey, answerKey }]
    let currentQuestionKey = null;
    let currentOutcomeKey = null;
    let resultIndex = 0;
    let started = false;
    let userActed = false;
    // True from the moment an answer is taken until the next screen is up.
    let answering = false;

    /* ------------------------------------------------------------------ *
     * Resuming
     *
     * A man in his sixties holding a phone taps the wrong thing, drags the
     * page down far enough to reload it, or follows a link and comes back.
     * Losing eleven answers to that is the difference between finishing and
     * giving up, and he will not start over.
     *
     * So the answers are kept on his own device and replayed when he returns.
     * Only the short answer keys the definition already uses are stored, under
     * a key belonging to this quiz, for a couple of hours. Nothing is sent
     * anywhere and nothing is written to the URL.
     * ------------------------------------------------------------------ */

    const PROGRESS_VERSION = 1;
    const PROGRESS_TTL_MS = 2 * 60 * 60 * 1000;
    const progressKey = `back9-quiz-progress:${config.quizKey || 'default'}`;

    // Private mode and blocked storage throw on access rather than return
    // null, so the store is probed once and then either exists or does not.
    const store = (() => {
      try {
        const probe = '__back9_probe__';
        window.localStorage.setItem(probe, '1');
        window.localStorage.removeItem(probe);
        return window.localStorage;
      } catch (error) {
        return null;
      }
    })();

    const forgetProgress = () => {
      try {
        store?.removeItem(progressKey);
      } catch (error) {
        /* Nothing to do, and nothing worth interrupting the quiz for. */
      }
    };

    const saveProgress = () => {
      if (!store || !started) return;
      try {
        store.setItem(progressKey, JSON.stringify({
          v: PROGRESS_VERSION,
          t: Date.now(),
          path: path.map((step) => ({ q: step.questionKey, a: step.answerKey, m: step.answerKeys })),
          r: resultIndex
        }));
      } catch (error) {
        /* A full or unavailable store is not a reason to stop. */
      }
    };

    const readProgress = () => {
      if (!store) return null;

      let saved = null;
      try {
        saved = JSON.parse(store.getItem(progressKey) || 'null');
      } catch (error) {
        return null;
      }

      if (!saved || saved.v !== PROGRESS_VERSION) return null;
      if (!Array.isArray(saved.path) || saved.path.length === 0) return null;
      if (typeof saved.t !== 'number' || Date.now() - saved.t > PROGRESS_TTL_MS) {
        forgetProgress();
        return null;
      }
      return saved;
    };

    // Walks the saved answers back through the live definition. Any step that
    // no longer fits — a question renamed, an answer edited, a branch pointed
    // somewhere else — throws the whole thing away rather than dropping a man
    // at a screen the definition would never have taken him to. Copy on this
    // quiz changes often, so this is the common case, not the rare one.
    const replayProgress = (saved) => {
      let key = model.startQuestionKey;
      const rebuilt = [];

      for (let index = 0; index < saved.path.length; index += 1) {
        const step = saved.path[index];
        if (!step || step.q !== key) return null;

        const question = model.questions.get(key);
        if (!question) return null;

        const answer = question.answers.find((candidate) => candidate.key === step.a);
        if (!answer) return null;

        const chosen = Array.isArray(step.m) && step.m.length ? step.m : [step.a];
        const allKnown = chosen.every((one) =>
          question.answers.some((candidate) => candidate.key === one));
        if (!allKnown) return null;

        rebuilt.push({ questionKey: step.q, answerKey: step.a, answerKeys: chosen });

        if (answer.next.type === 'OUTCOME') {
          // Nothing can follow an outcome, so a longer path is a broken one.
          if (index !== saved.path.length - 1) return null;
          return { path: rebuilt, outcomeKey: answer.next.key };
        }

        key = answer.next.key;
      }

      return { path: rebuilt, questionKey: key };
    };

    const placeBack = (view, handler) => {
      const back = element('button', 'quiz__back', config.backLabel);
      back.type = 'button';
      back.addEventListener('click', handler);
      if (backSlot) backSlot.replaceChildren(back);
      else view.append(back);
    };

    const clearBack = () => {
      if (backSlot) backSlot.replaceChildren();
    };

    const swap = (node, focusTarget) => {
      answering = false;
      const animate = !reduceMotion();
      stage.replaceChildren(node);
      if (animate) {
        node.classList.add('quiz__view--enter');
        window.requestAnimationFrame(() => node.classList.remove('quiz__view--enter'));
      }
      // Focus follows the reader from one step to the next, but only once the
      // reader has actually acted. Grabbing focus on first paint would yank a
      // visitor out of wherever they were on the page.
      if (focusTarget && userActed) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
    };

    const keepCardInView = () => {
      if (!started || !card) return;
      const top = card.getBoundingClientRect().top;
      if (top >= 0) return;
      card.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    };

    const updateProgress = () => {
      if (!config.showProgress || !progress) return;
      if (!currentQuestionKey) {
        progress.hidden = true;
        return;
      }

      const answered = path.length;
      const left = model.remaining.get(currentQuestionKey);
      const total = typeof left === 'number' ? answered + left : answered + 1;
      const percent = Math.round((answered / Math.max(total, 1)) * 100);
      const label = config.progressText
        .replace('[[ current ]]', String(answered + 1))
        .replace('[[ total ]]', String(total));

      progress.hidden = false;
      if (progressMeter) {
        progressMeter.setAttribute('aria-valuenow', String(percent));
        progressMeter.setAttribute('aria-valuetext', label);
      }
      if (progressBar) progressBar.style.setProperty('--quiz-progress', `${percent}%`);
      if (progressLabel) progressLabel.textContent = label;
      if (countLabel) countLabel.textContent = `${answered + 1} / ${total}`;
    };

    const renderMessage = (heading, body, link) => {
      const view = element('div', 'quiz__view quiz__view--message');
      const title = element('h3', 'quiz__message-heading', heading);
      view.append(title);
      if (body) view.append(element('p', 'quiz__message-text', body));
      if (link && link.url && link.label) {
        const anchor = element('a', 'button quiz__message-button', link.label);
        anchor.href = link.url;
        view.append(anchor);
      }
      if (progress) progress.hidden = true;
      swap(view, title);
    };

    const renderIntro = () => {
      const view = element('div', 'quiz__view quiz__view--intro');
      const title = element('h3', 'quiz__intro-heading', config.introHeading || model.name);
      view.append(title);
      if (config.introText) view.append(element('p', 'quiz__intro-text', config.introText));

      const button = element('button', 'button button--full quiz__start', config.startLabel);
      button.type = 'button';
      button.addEventListener('click', () => {
        started = true;
        userActed = true;
        goToQuestion(model.startQuestionKey);
      });
      view.append(button);

      if (config.introFootnote) view.append(element('p', 'quiz__footnote', config.introFootnote));
      if (progress) progress.hidden = true;
      swap(view, title);
    };

    // Shared questions are repeated once per branch and keyed `token-branch`.
    // Matching on prefix is wrong — `liner` would claim `liner-fact-cart` —
    // so the branch name is stripped from the end using the known list.
    // Suffixes are `-lane` before the split and `-lane--group` after it, and
    // one is a suffix of the other, so the longest match has to win.
    const branchSuffixes = [...(config.lanes || [])].sort((a, b) => b.length - a.length);
    const tokenOf = (key) => {
      const branch = branchSuffixes.find((name) => key.endsWith(`-${name}`));
      return branch ? key.slice(0, -(branch.length + 1)) : key;
    };

    const layoutFor = (key) => (config.screens || {})[tokenOf(key)] || 'list';

    const selectionFor = (key) => (config.selections || {})[tokenOf(key)] || null;

    // A seeding card: one screen, one button, no choice to make. It exists to
    // land a fact before the next question rather than to collect an answer.
    const renderCard = (question, answer) => {
      const view = element('div', 'quiz__view quiz__view--card');
      const heading = element('h3', 'quiz__card-heading', question.prompt);
      view.append(heading);
      if (question.media) view.append(image(question.media, 'quiz__media quiz__media--card'));
      if (question.supportingText) view.append(element('p', 'quiz__card-text', question.supportingText));

      const button = element('button', 'button button--full quiz__card-button', answer.label || 'Continue');
      button.type = 'button';
      button.addEventListener('click', () => choose(question, answer, button));
      view.append(button);

      if (path.length > 0) placeBack(view, goBack);
      else clearBack();

      swap(view, heading);
      announce(question.prompt);
      updateProgress();
      keepCardInView();
    };

    const renderQuestion = (question) => {
      const layout = layoutFor(question.key);
      if (layout === 'card' && question.answers.length === 1) {
        renderCard(question, question.answers[0]);
        return;
      }

      const view = element('div', 'quiz__view quiz__view--question');
      const promptId = `QuizPrompt-${config.sectionId}`;

      const prompt = element('h3', 'quiz__prompt', question.prompt);
      prompt.id = promptId;
      view.append(prompt);

      if (question.supportingText) view.append(element('p', 'quiz__support', question.supportingText));
      if (question.media) view.append(image(question.media, 'quiz__media'));

      const multi = selectionFor(question.key);
      const options = element('div', `quiz__options quiz__options--${layout}${multi ? ' quiz__options--multi' : ''}`);
      options.setAttribute('role', 'group');
      options.setAttribute('aria-labelledby', promptId);

      question.answers.forEach((answer, index) => {
        const button = element('button', 'quiz__option');
        button.type = 'button';
        button.dataset.index = String(index);
        if (multi) {
          button.append(element('span', 'quiz__option-marker quiz__option-marker--box', ''));
        } else if (layout === 'list' || layout === 'grid') {
          button.append(element('span', 'quiz__option-marker', String.fromCharCode(65 + index)));
        }
        button.append(element('span', 'quiz__option-label', answer.label));
        if (!multi) button.addEventListener('click', (event) => choose(question, answer, button, event));
        options.append(button);
      });

      // Arrow keys move within the group; the buttons stay individually
      // tabbable so nothing is trapped.
      options.addEventListener('keydown', (event) => {
        const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
        if (!keys.includes(event.key)) return;
        const buttons = [...options.querySelectorAll('.quiz__option')];
        const active = buttons.indexOf(document.activeElement);
        if (active === -1) return;
        event.preventDefault();
        const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
        const next = (active + (forward ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next].focus();
      });

      view.append(options);

      if (multi) {
        const confirm = element('button', 'button button--full quiz__confirm', config.continueLabel || 'Continue');
        confirm.type = 'button';
        confirm.disabled = true;

        const chosen = new Set();
        const syncConfirm = () => {
          const enough = chosen.size >= (multi.minSelections || 1);
          confirm.disabled = !enough;
          confirm.classList.toggle('quiz__confirm--ready', enough);
        };

        options.querySelectorAll('.quiz__option').forEach((button, index) => {
          const answer = question.answers[index];
          button.setAttribute('role', 'checkbox');
          button.setAttribute('aria-checked', 'false');
          button.addEventListener('click', (event) => {
            const on = chosen.has(answer.key);
            if (!on && chosen.size >= (multi.maxSelections || question.answers.length)) return;
            if (on) chosen.delete(answer.key); else chosen.add(answer.key);

            button.classList.toggle('quiz__option--chosen', !on);
            button.setAttribute('aria-checked', String(!on));
            const box = button.querySelector('.quiz__option-marker');
            if (box) box.textContent = !on ? '\u2713' : '';
            if (!on) rippleFrom(button, event);
            syncConfirm();
          });
        });

        confirm.addEventListener('click', () => {
          if (confirm.disabled) return;
          userActed = true;
          const picked = question.answers.filter((answer) => chosen.has(answer.key));
          // Every answer on a shared question points at the same next screen,
          // so any pick advances identically. On the closing question the last
          // one listed decides the result group.
          advance(question, picked[picked.length - 1], picked.map((answer) => answer.key));
        });

        view.append(confirm);
      }

      if (path.length > 0) placeBack(view, goBack);
      else clearBack();

      swap(view, prompt);
      announce(question.prompt);
      updateProgress();
      keepCardInView();
    };

    // The label a visitor chose for a given question, found by the question's
    // token. Shared questions are repeated once per branch — `shoes-cart`,
    // `shoes-walking` — so a token matches its own key or that key plus a
    // branch suffix.
    const stepFor = (token) => path.find((entry) => tokenOf(entry.questionKey) === token);

    // The button labels, exactly as the visitor read them. Used by the
    // summary, which is a record of what he said.
    const labelsFor = (token) => {
      const step = stepFor(token);
      if (!step) return [];
      const question = model.questions.get(step.questionKey);
      const keys = step.answerKeys || [step.answerKey];
      return keys
        .map((key) => question?.answers.find((option) => option.key === key)?.label)
        .filter(Boolean);
    };

    const labelFor = (token) => labelsFor(token)[0] || null;

    const joinList = (items) => {
      if (items.length === 0) return '';
      if (items.length === 1) return items[0];
      return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
    };

    // The same answers worded to sit inside a sentence. "Something else" is a
    // fine button and a poor noun, so in copy it reads "your golf shoes". A
    // multi-select contributes every answer it took, joined as a list.
    const answerFor = (token) => {
      const step = stepFor(token);
      if (!step) return null;
      const question = model.questions.get(step.questionKey);
      const overrides = config.tokenOverrides?.[token] || {};
      const wordings = (step.answerKeys || [step.answerKey])
        .map((key) => overrides[key] || question?.answers.find((option) => option.key === key)?.label)
        .filter(Boolean);
      return wordings.length > 0 ? joinList(wordings) : labelFor(token);
    };

    // Lets result copy name what the visitor actually said — "the liner inside
    // your [[ shoes ]]" — without needing a separate outcome per combination.
    // Purely local: it reads the answers already on screen and sends nothing.
    const fillTokens = (copy) => copy.replace(/\[\[\s*([a-z0-9_-]+)\s*\]\]/gi, (match, token) => {
      const label = answerFor(token);
      return label === null ? match : label;
    });

    // The summary is the personalisation. Reading his own answers back as a
    // labelled report is what makes the result feel worked out; the copy
    // underneath it is the same for everyone.
    const renderRecap = (asHeading, outcome) => {
      if (!config.showRecap) return null;

      const rows = (Array.isArray(config.summaryRows) ? config.summaryRows : [])
        .map((row) => ({ label: row.label, values: labelsFor(row.token) }))
        .filter((row) => row.values.length > 0);

      const outlook = outcome?.recapOutlook || config.recapOutlook;
      if (rows.length === 0 && !config.recapIntro && !outlook) return null;

      const recap = element('div', 'quiz__recap');
      if (config.recapLabel) {
        recap.append(element(asHeading ? 'h3' : 'p', 'quiz__recap-label', config.recapLabel));
      }
      // One point per line, arriving in order. A recap that assembles itself
      // reads as something built for him; the same words in one paragraph read
      // as a sentence he has to unpick.
      const points = String(config.recapIntro || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      if (points.length === 1) {
        recap.append(element('p', 'quiz__recap-intro', fillTokens(points[0])));
      } else if (points.length > 1) {
        const list = element('ul', 'quiz__recap-points');
        points.forEach((point, index) => {
          const item = element('li', 'quiz__recap-point', fillTokens(point));
          item.style.setProperty('--recap-delay', `${index * 140}ms`);
          list.append(item);
        });
        recap.append(list);
      }

      if (rows.length > 0) {
        const list = element('dl', 'quiz__recap-list');
        rows.forEach((row) => {
          list.append(element('dt', 'quiz__recap-key', row.label));
          list.append(element('dd', 'quiz__recap-value', row.values.join(' \u00B7 ')));
        });
        recap.append(list);
      }

      // What the answers mean for him next, rather than what he just said.
      // The caution mark sits in the same column as the bullets above it, so
      // the line reads as the conclusion those points arrive at.
      if (outlook) {
        const line = element('p', 'quiz__recap-outlook');
        line.append(alertMark(), element('span', 'quiz__recap-outlook-text', fillTokens(outlook)));
        recap.append(line);
      }
      return recap;
    };

    // A pause before the result, so it reads as worked out rather than as a
    // page that was always going to say this. Deliberately short.
    const renderProcessing = (onDone) => {
      const wait = reduceMotion() ? 0 : Number(config.processingMs) || 0;
      if (wait <= 0) return onDone();

      const view = element('div', 'quiz__view quiz__view--processing');
      view.append(element('p', 'quiz__processing-text', config.processingText || ''));
      const track = element('div', 'quiz__processing-track');
      const bar = element('span', 'quiz__processing-bar');
      bar.style.setProperty('--processing-ms', `${wait}ms`);
      track.append(bar);
      view.append(track);

      // Work ticking over one line at a time reads as work being done, where a
      // bar alone reads as waiting. Each step lands, then takes its tick.
      const steps = String(config.processingSteps || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      const timers = [];
      if (steps.length > 0) {
        const list = element('ul', 'quiz__processing-steps');
        const slice = wait / (steps.length + 1);
        steps.forEach((label, index) => {
          const item = element('li', 'quiz__processing-step');
          item.append(element('span', 'quiz__processing-tick', '\u2713'));
          item.append(element('span', 'quiz__processing-label', label));
          list.append(item);
          timers.push(window.setTimeout(() => item.classList.add('is-shown'), slice * index + 60));
          timers.push(window.setTimeout(() => item.classList.add('is-done'), slice * (index + 1)));
        });
        view.append(list);
      }

      if (progress) progress.hidden = true;
      swap(view, null);
      announce(config.processingText || '');
      timers.push(window.setTimeout(() => {
        timers.forEach((id) => window.clearTimeout(id));
        onDone();
      }, wait));
    };

    const ctaButton = (outcome) => {
      const cta = element('a', 'button button--full quiz__cta', fillTokens(outcome.ctaLabel || config.fallbackLabel));
      cta.href = outcome.destinationPath;
      // Navigation happens immediately. The CTA event goes out on the way and
      // is never waited on.
      cta.addEventListener('click', () => {
        tracker?.cta(path.length);
        sheet?.cta(path.length, outcome.key);
      });
      return cta;
    };

    // The quiz itself is over by the time any result screen shows, so the bar
    // stays full rather than running backwards from where the last question
    // left it.
    const resultProgress = () => {
      if (!progress || !config.showProgress) return;
      progress.hidden = false;
      if (progressMeter) {
        progressMeter.setAttribute('aria-valuenow', '100');
        progressMeter.setAttribute('aria-valuetext', config.progressCompleteText);
      }
      if (progressBar) progressBar.style.setProperty('--quiz-progress', '100%');
      if (progressLabel) progressLabel.textContent = config.progressCompleteText;
    };

    // Result copy is long enough to need paragraphs. A blank line in the
    // setting starts a new one; a single newline is just a wrap.
    // A paragraph that is only [figure:name] places that drawing at that
    // point in the copy, so an illustration can sit between two paragraphs
    // rather than only above the whole passage.
    const FIGURE_LINE = /^\[figure:([a-z0-9-]+)\]$/i;

    const paragraphs = (copy, className) =>
      String(copy || '')
        .split(/\n\s*\n/)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
          const figure = part.match(FIGURE_LINE);
          if (!figure) return element('p', className, fillTokens(part));
          const url = mediaUrl(config.figures?.[figure[1].toLowerCase()] || '');
          if (!url) return null;
          const media = image({ url, alt: '' }, 'quiz__media quiz__media--figure');
          media.alt = config.figureAlt?.[figure[1].toLowerCase()] || '';
          return media;
        })
        .filter(Boolean);

    // "Step 1:", "Step 2:" are supplied by the list itself, so the setting
    // holds only the steps and renumbering never means retyping them.
    const renderProtocol = (outcome) => {
      const steps = String(outcome.protocolSteps || '')
        .split('\n')
        .map((line) => line.replace(/^\s*step\s*\d+\s*[:.\-–]\s*/i, '').trim())
        .filter(Boolean);
      if (steps.length === 0) return null;

      const wrap = element('div', 'quiz__protocol');
      if (outcome.protocolHeading) {
        wrap.append(element('p', 'quiz__protocol-heading', fillTokens(outcome.protocolHeading)));
      }
      const list = element('ol', 'quiz__protocol-list');
      steps.forEach((step, position) => {
        const item = element('li', 'quiz__protocol-step');
        item.append(element('span', 'quiz__protocol-number', String(position + 1)));
        item.append(element('span', 'quiz__protocol-text', fillTokens(step)));
        list.append(item);
      });
      wrap.append(list);
      if (outcome.protocolClose) {
        wrap.append(element('p', 'quiz__protocol-close', fillTokens(outcome.protocolClose)));
      }
      return wrap;
    };

    const renderTestimonial = () => {
      const quote = config.testimonial || {};
      if (!quote.body && !quote.heading) return null;

      const card = element('figure', 'quiz__testimonial');

      // Photo, stars, headline and name are one block — a face beside the
      // claim. The quote itself runs full width beneath them.
      const head = element('div', 'quiz__testimonial-head');
      const photoUrl = mediaUrl(quote.image || '');
      if (photoUrl) {
        head.append(image({ url: photoUrl, alt: quote.imageAlt || '' }, 'quiz__testimonial-photo'));
      }

      const meta = element('div', 'quiz__testimonial-meta');
      const rating = Math.max(0, Math.min(5, Number(quote.rating) || 0));
      if (rating > 0) {
        const stars = element('div', 'quiz__stars', '\u2605'.repeat(rating));
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', `${rating} out of 5 stars`);
        meta.append(stars);
      }
      if (quote.heading) meta.append(element('p', 'quiz__testimonial-heading', quote.heading));
      if (quote.author) meta.append(element('figcaption', 'quiz__testimonial-author', quote.author));
      head.append(meta);
      card.append(head);

      if (quote.body) {
        const body = element('blockquote', 'quiz__testimonial-body');
        paragraphs(quote.body, 'quiz__testimonial-line').forEach((line) => body.append(line));
        card.append(body);
      }
      return card;
    };

    const renderResultScreen = (outcome, index) => {
      const screens = Array.isArray(config.resultScreens) ? config.resultScreens : [];
      const screen = screens[index - 1];
      if (!screen) return renderResult(outcome);

      resultIndex = index;
      saveProgress();
      const view = element('div', 'quiz__view quiz__view--result');
      const title = element('h3', 'quiz__result-title', fillTokens(screen.heading || ''));
      view.append(title);

      const media = normalizeMedia(screen.image
        ? { type: 'SHOPIFY_CDN_IMAGE', url: screen.image, alt: screen.imageAlt }
        : null);
      if (media) view.append(image(media, 'quiz__media quiz__media--result'));
      if (screen.body) view.append(element('p', 'quiz__result-body', fillTokens(screen.body)));

      const last = index >= screens.length;
      if (last && outcome.destinationPath) {
        if (config.resultProof) view.append(element('p', 'quiz__result-proof', fillTokens(config.resultProof)));
        view.append(ctaButton(outcome));
        if (config.resultReassurance) view.append(element('p', 'quiz__reassurance', config.resultReassurance));
      } else {
        const next = element('button', 'button button--full quiz__continue', config.continueLabel || 'Continue');
        next.type = 'button';
        next.addEventListener('click', () => {
          userActed = true;
          renderResultScreen(outcome, index + 1);
        });
        view.append(next);
      }

      placeBack(view, () => {
        userActed = true;
        if (index <= 1) renderResult(outcome);
        else renderResultScreen(outcome, index - 1);
      });

      resultProgress();
      swap(view, title);
      announce(screen.heading || '');
      keepCardInView();
    };

    const renderResult = (outcome) => {
      resultIndex = 0;
      const screens = Array.isArray(config.resultScreens) ? config.resultScreens : [];
      const view = element('div', 'quiz__view quiz__view--result');

      if (outcome.eyebrow) view.append(element('p', 'eyebrow quiz__result-eyebrow', outcome.eyebrow));

      const hasTitle = Boolean(outcome.title);
      let title = null;
      if (hasTitle) {
        title = element('h3', 'quiz__result-title', fillTokens(outcome.title));
        view.append(title);
      }

      const recap = renderRecap(!hasTitle, outcome);
      if (recap) {
        view.append(recap);
        if (!title) title = recap.querySelector('h3');
      }

      if (outcome.media) view.append(image(outcome.media, 'quiz__media quiz__media--result'));
      paragraphs(outcome.body, 'quiz__result-body').forEach((part) => view.append(part));

      const protocol = renderProtocol(outcome);
      if (protocol) view.append(protocol);

      if (config.resultProof) view.append(element('p', 'quiz__result-proof', fillTokens(config.resultProof)));

      const testimonial = renderTestimonial();
      if (testimonial) view.append(testimonial);

      if (screens.length > 0) {
        const next = element('button', 'button button--full quiz__continue', config.continueLabel || 'Continue');
        next.type = 'button';
        next.addEventListener('click', () => {
          userActed = true;
          renderResultScreen(outcome, 1);
        });
        view.append(next);
      } else if (outcome.destinationPath) {
        if (config.ctaLead) view.append(element('p', 'quiz__cta-lead', fillTokens(config.ctaLead)));

        // The button and the promise underneath it are one thing, so they ride
        // the bottom of the screen together. Left loose in the flow, the
        // guarantee sat below a button that never moves, which parked it
        // permanently off the bottom of the screen — unread at the exact
        // moment it is doing the most work.
        const dock = element('div', 'quiz__cta-dock');
        dock.append(ctaButton(outcome));
        if (config.resultReassurance) {
          dock.append(element('p', 'quiz__reassurance', config.resultReassurance));
        }
        view.append(dock);
      }

      if (config.showRestart) {
        const restart = element('button', 'quiz__restart', config.restartLabel);
        restart.type = 'button';
        restart.addEventListener('click', () => {
          userActed = true;
          tracker?.retake();
          path = [];
          currentOutcomeKey = null;
          forgetProgress();
          goToQuestion(model.startQuestionKey);
        });
        view.append(restart);
      }

      if (config.resultFootnote) view.append(element('p', 'quiz__footnote', config.resultFootnote));

      resultProgress();
      swap(view, title);
      announce(`${config.progressCompleteText}. ${outcome.title || config.recapLabel || ''}`.trim());
      keepCardInView();

      // Only confirmed after the result is actually on screen.
      tracker?.complete(path.length);
      sheet?.complete(path.length, currentOutcomeKey);
    };

    const goToQuestion = (key) => {
      const question = model.questions.get(key);
      if (!question) {
        renderMessage(config.errorHeading, config.errorText, {
          url: config.fallbackUrl,
          label: config.fallbackLabel
        });
        return;
      }
      currentQuestionKey = key;
      currentOutcomeKey = null;
      renderQuestion(question);
    };

    const goToOutcome = (key) => {
      const outcome = model.outcomes.get(key);
      if (!outcome) {
        renderMessage(config.errorHeading, config.errorText, {
          url: config.fallbackUrl,
          label: config.fallbackLabel
        });
        return;
      }
      currentQuestionKey = null;
      currentOutcomeKey = key;
      renderProcessing(() => renderResult(outcome));
    };

    const advance = (question, answer, answerKeys) => {
      const index = path.length;
      path.push({
        questionKey: question.key,
        answerKey: answer.key,
        answerKeys: answerKeys || [answer.key]
      });
      tracker?.answer(index, question.key, answer.key);
      sheet?.answer(index, question.key, answer.key, answerKeys || [answer.key]);
      saveProgress();

      if (answer.next.type === 'OUTCOME') goToOutcome(answer.next.key);
      else goToQuestion(answer.next.key);
    };

    const rippleFrom = (button, event) => {
      if (reduceMotion()) return;
      const rect = button.getBoundingClientRect();
      const ripple = element('span', 'quiz__ripple');
      const x = event && event.clientX ? event.clientX - rect.left : rect.width / 2;
      const y = event && event.clientY ? event.clientY - rect.top : rect.height / 2;
      ripple.style.setProperty('--ripple-x', `${x}px`);
      ripple.style.setProperty('--ripple-y', `${y}px`);
      button.append(ripple);
      window.setTimeout(() => ripple.remove(), 500);
    };

    const choose = (question, answer, button, event) => {
      if (answering) return;
      answering = true;
      userActed = true;
      button.dataset.chosen = 'true';
      button.setAttribute('aria-disabled', 'true');

      // Every other answer on this screen stops responding at the same moment,
      // so the one that was tapped is the only one that can count.
      const view = button.closest('.quiz__view');
      if (view) {
        view.querySelectorAll('.quiz__option, .quiz__confirm, .quiz__card-button').forEach((other) => {
          if (other === button) return;
          other.setAttribute('aria-disabled', 'true');
          other.style.pointerEvents = 'none';
        });
      }

      const isAnswer = button.classList.contains('quiz__option');
      if (isAnswer) {
        button.classList.add('quiz__option--chosen');
        const marker = button.querySelector('.quiz__option-marker');
        if (marker) marker.textContent = '\u2713';
      }

      // A chosen answer should feel like it landed. A ripple from where the
      // thumb actually hit, then a beat on the filled state before the swap —
      // enough to register as a result, short enough not to feel like waiting.
      // A plain Continue has no state to show off, so it just goes.
      rippleFrom(button, event);

      const delay = reduceMotion() ? 0 : (isAnswer ? 340 : 140);
      window.setTimeout(() => advance(question, answer), delay);
    };

    const goBack = () => {
      if (path.length === 0) return;
      userActed = true;
      const previous = path.pop();
      saveProgress();
      goToQuestion(previous.questionKey);
    };

    const boot = async () => {
      renderMessage(config.loadingHeading, '', null);

      // The definition is fetched for everyone, consented or not. It creates
      // no session, answer or rollup.
      const definitionResponse = await api.definition({ quizKey: config.quizKey });

      if (!definitionResponse.ok) {
        renderMessage(config.errorHeading, config.errorText, {
          url: config.fallbackUrl,
          label: config.fallbackLabel
        });
        return;
      }

      model = buildModel(definitionResponse.body);
      if (!model || model.errors.length > 0) {
        if (config.designMode) {
          renderMessage(config.errorHeading, (model?.errors || ['The quiz definition could not be read.']).join(' '), null);
          return;
        }
        if (!model || !model.questions.has(model.startQuestionKey)) {
          renderMessage(config.errorHeading, config.errorText, {
            url: config.fallbackUrl,
            label: config.fallbackLabel
          });
          return;
        }
        // Outside the editor a partially invalid graph still runs; a branch
        // that dead-ends falls through to the message view with a real link.
      }

      // Tracking is entirely optional. Its absence — file not loaded, consent
      // not granted, storage unavailable — is a normal state, not an error,
      // and the quiz below never checks again.
      if (window.Back9QuizTracking) {
        tracker = await window.Back9QuizTracking
          .create({ api, config, model, challengeSlot })
          .catch(() => null);
      }

      // Absent endpoint, malformed endpoint, consent withheld: all normal, and
      // all silent. Nothing below this line ever checks again.
      try {
        sheet = window.Back9QuizSheet?.create({ config }) || null;
      } catch (error) {
        sheet = null;
      }
      sheet?.start();

      const saved = readProgress();
      const resumed = saved ? replayProgress(saved) : null;

      if (resumed) {
        // He is coming back to a quiz he had already started. The intro and
        // the pause before the result have both already happened for him, so
        // neither is played again.
        path = resumed.path;
        started = true;

        if (!resumed.outcomeKey) {
          goToQuestion(resumed.questionKey);
          return;
        }

        const outcome = model.outcomes.get(resumed.outcomeKey);
        if (outcome) {
          currentOutcomeKey = resumed.outcomeKey;
          const screen = Number(saved.r);
          if (Number.isFinite(screen) && screen > 0) renderResultScreen(outcome, screen);
          else renderResult(outcome);
          return;
        }

        path = [];
        started = false;
      }

      if (saved) forgetProgress();

      if (config.showIntro) {
        renderIntro();
      } else {
        started = true;
        goToQuestion(model.startQuestionKey);
      }
    };

    boot().catch((error) => {
      // A reader still gets a real link out, but this catch would otherwise
      // turn a genuine bug into a silent "unavailable", so it is reported.
      if (config.debug) console.error('[back9-quiz] boot failed:', error);
      renderMessage(config.errorHeading, config.errorText, {
        url: config.fallbackUrl,
        label: config.fallbackLabel
      });
    });
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  // The section injects its scripts in order, but a section added live in the
  // theme editor can land out of order, so wait for the adapter rather than
  // assuming it. Tracking is optional, so it only gets a short grace period
  // before we start without it.
  const TRACKING_GRACE_MS = 1000;

  const whenReady = (callback, waited = 0) => {
    if (window.Back9QuizApi && (window.Back9QuizTracking || waited >= TRACKING_GRACE_MS)) return callback();
    if (waited > 5000) return;
    window.setTimeout(() => whenReady(callback, waited + 50), 50);
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('[data-back9-quiz]').forEach((root) => {
      whenReady(() => createQuiz(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
